import React, { useState, useEffect, useRef } from 'react';
import { Lock, User, Eye, EyeOff, ShieldCheck, Wrench, AlertCircle, KeyRound, Check, HelpCircle, ArrowLeft, RefreshCw, Phone, Mail, ShieldAlert, Loader2, UserCheck, Database, Server } from 'lucide-react';
import { AppUser, CompanyInfo } from '../types';
import { getStoredUsers, saveCompanyInfo, saveUsers, setCurrentAuthVersion, setCurrentUser } from '../utils/storage';

interface LoginScreenProps {
  companyInfo: CompanyInfo;
  onLoginSuccess: (authVersion?: string, user?: AppUser) => void;
  onSaveCompanyInfo?: (info: CompanyInfo) => void;
  sessionExpiredMessage?: string;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({
  companyInfo: initialCompanyInfo,
  onLoginSuccess,
  onSaveCompanyInfo,
  sessionExpiredMessage,
}) => {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(initialCompanyInfo);
  const [users, setUsers] = useState<AppUser[]>(getStoredUsers());
  const [isVerifying, setIsVerifying] = useState(false);

  // Mode: 'login' | 'forgot' | 'reset'
  const [mode, setMode] = useState<'login' | 'forgot' | 'reset' | 'setup'>('login');

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  // Secure first-time MySQL setup state
  const [setupKey, setSetupKey] = useState('');
  const [dbHost, setDbHost] = useState('localhost');
  const [dbPort, setDbPort] = useState('3306');
  const [dbName, setDbName] = useState('');
  const [dbUser, setDbUser] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [setupError, setSetupError] = useState('');
  const [setupSuccess, setSetupSuccess] = useState('');

  // Forgot password verification state
  const [forgotUsername, setForgotUsername] = useState('');
  const [securityAnswerInput, setSecurityAnswerInput] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [resetToken, setResetToken] = useState('');

  // Reset password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Kurulum durumu isteklerinde eski (stale) cevabın yeni durumu ezmesini engeller.
  const setupStatusRequestRef = useRef(0);


  // SQL bağlantısı yoksa login yerine güvenli ilk-kurulum ekranını göster.
  useEffect(() => {
    let active = true;
    const requestId = ++setupStatusRequestRef.current;
    fetch('/api.php?action=setup-status', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then(async (res) => {
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) return null;
        return res.json().catch(() => null);
      })
      .then((data) => {
        // Kurulum kaydı sırasında daha eski bir setup-status cevabı geç gelirse yok say.
        if (!active || requestId !== setupStatusRequestRef.current || !data) return;
        setMode(data.setupRequired ? 'setup' : 'login');
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const handleSetupDatabase = async (e: React.FormEvent) => {
    e.preventDefault();
    setSetupError('');
    setSetupSuccess('');
    if (!setupKey.trim() || !dbName.trim() || !dbUser.trim() || !dbPassword) {
      setSetupError('Kurulum anahtarı, veritabanı adı, kullanıcı adı ve parola zorunludur.');
      return;
    }
    setIsVerifying(true);
    try {
      const res = await fetch('/api.php?action=setup-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({
          setupKey: setupKey.trim(), host: dbHost.trim() || 'localhost', port: Number(dbPort) || 3306,
          database: dbName.trim(), user: dbUser.trim(), password: dbPassword,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setSetupError(data?.message || 'MySQL kurulumu kaydedilemedi.');
        return;
      }
      setSetupSuccess(data.message || 'MySQL bağlantısı kuruldu.');
      setDbPassword('');

      // Sayfayı F5 ile yeniletmeye güvenme. Kurulumdan önce başlamış olabilecek
      // setup-status isteğini geçersiz kıl ve yeni PHP isteğinde config'in gerçekten
      // aktif olduğunu doğrula. Başarılıysa aynı ekranda doğrudan normal login'e geç.
      ++setupStatusRequestRef.current;
      let setupStillRequired = true;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise(resolve => setTimeout(resolve, 150));
        const statusRes = await fetch(`/api.php?action=setup-status&_=${Date.now()}_${attempt}`, {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        }).catch(() => null);
        if (!statusRes) continue;
        const ct = statusRes.headers.get('content-type') || '';
        if (!ct.toLowerCase().includes('application/json')) continue;
        const statusData = await statusRes.json().catch(() => null);
        if (statusRes.ok && statusData && statusData.setupRequired === false) {
          setupStillRequired = false;
          break;
        }
      }

      if (!setupStillRequired) {
        setSetupSuccess('MySQL bağlantısı aktif. Normal giriş ekranına geçildi.');
        setSetupKey('');
        setMode('login');
      } else {
        // Nadir shared-hosting opcode/config gecikmelerinde temiz yeni istek için fallback.
        window.location.replace(`${window.location.pathname}?dbsetup=ok&t=${Date.now()}`);
      }
    } catch {
      setSetupError('api.php erişilemedi. Sunucu/PHP yapılandırmasını kontrol edin.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Login ekranı açılırken korumalı veri endpoint'ine istek atma.
  // Önceki sürümde buradaki action=get isteği 401 dönüp başarılı ilk girişle
  // yarışarak yeni oturumu tarayıcı tarafında silebiliyordu. Sunucu verileri
  // yalnız başarılı girişten sonra App tarafından senkronize edilir.

  // Normal Login with Server Authentication (Secure API)
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const cleanInputUser = username.trim();
    const cleanInputPass = password.trim();

    if (!cleanInputUser || !cleanInputPass) {
      setError('Lütfen kullanıcı adı ve şifrenizi giriniz.');
      return;
    }

    setIsVerifying(true);

    try {
      // Shared-hosting/Hostinger: authenticate directly through PHP.
      // Do not try /api/login first: SPA rewrites can return index.html with HTTP 200,
      // which looks successful but is not JSON and prevents the PHP fallback.
      const loginRes = await fetch('/api.php?action=login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({ username: cleanInputUser, password: cleanInputPass }),
      }).catch(() => null);

      if (loginRes) {
        const contentType = loginRes.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('application/json')) {
          if (loginRes.status === 404) {
            setError('api.php bulunamadı (HTTP 404). Hostinger public_html kökünde api.php dosyasının bulunduğunu kontrol edin.');
          } else {
            setError(`api.php JSON yerine HTML döndürüyor (HTTP ${loginRes.status}). Sunucu/PHP yönlendirme ayarlarını kontrol edin.`);
          }
          return;
        }

        const loginData = await loginRes.json().catch(() => null);
        if (loginRes.ok && loginData?.success && loginData?.user) {
          const authenticatedUser: AppUser = loginData.user;
          const authVersion = loginData.passwordVersion || companyInfo.passwordVersion || '1';

          setCurrentAuthVersion(authVersion);
          setCurrentUser(authenticatedUser);

          if (rememberMe) {
            localStorage.setItem('ts_auth_remember', 'true');
          }
          sessionStorage.setItem('ts_auth', 'true');

          onLoginSuccess(authVersion, authenticatedUser);
          return;
        }

        // 400/401/403 yanında 429, 500 ve özellikle 503 MySQL hatalarını da
        // kullanıcıya gerçek sunucu mesajıyla göster. Böylece bağlantı problemi
        // yanlışlıkla "internet/api.php erişimi" hatası olarak görünmez.
        if (!loginRes.ok || loginData?.success === false) {
          const fallback = loginRes.status === 503
            ? 'MySQL bağlantısı kurulamadı. private_config.php ve veritabanı erişimini kontrol edin.'
            : loginRes.status === 429
              ? 'Çok fazla giriş denemesi yapıldı. Lütfen daha sonra tekrar deneyin.'
              : 'Sunucu giriş isteğini reddetti.';
          setError(loginData?.message || `${fallback} (HTTP ${loginRes.status})`);
          return;
        }
      }
    } catch (err: any) {
      console.warn('Server auth request error:', err);
    } finally {
      setIsVerifying(false);
    }

    // Güvenlik: parola doğrulaması yalnızca sunucuda yapılır. Tarayıcıdaki yerel kullanıcı/parola kopyalarıyla giriş yapılmaz.
    setError('Sunucuya güvenli giriş yapılamadı. İnternet bağlantısını ve api.php erişimini kontrol edin.');
  };

  // Verify Identity for Forgot Password via Secure Server API
  const handleVerifyIdentity = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');

    const inputUser = forgotUsername.trim();
    const cleanSecAnswerInput = securityAnswerInput.trim();

    if (!inputUser) {
      setForgotError('Lütfen kullanıcı adınızı giriniz.');
      return;
    }
    if (!cleanSecAnswerInput) {
      setForgotError('Lütfen gizli güvenlik cevabını veya kurtarma kodunu giriniz.');
      return;
    }

    setIsVerifying(true);
    try {
      const verifyRes = await fetch('/api.php?action=verify_security', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({ username: inputUser, securityAnswer: cleanSecAnswerInput }),
      }).catch(() => null);

      if (verifyRes && verifyRes.ok) {
        const verifyData = await verifyRes.json().catch(() => null);
        if (!verifyData?.resetToken) {
          setForgotError('Güvenli sıfırlama anahtarı alınamadı.');
          return;
        }
        setResetToken(verifyData.resetToken);
        setMode('reset');
        return;
      } else if (verifyRes) {
        const errData = await verifyRes.json().catch(() => null);
        setForgotError(errData?.message || 'Girdiğiniz güvenlik cevabı veya kurtarma kodu hatalı!');
        return;
      }
    } catch (err) {
      console.warn('Security verify error:', err);
    } finally {
      setIsVerifying(false);
    }

    setForgotError('Sunucu doğrulaması yapılamadı. Güvenlik nedeniyle çevrimdışı parola sıfırlama kapalıdır.');
  };

  // Reset Password via Secure Server API
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');

    const cleanPass = newPassword.trim();
    if (!cleanPass) {
      setForgotError('Lütfen yeni şifrenizi giriniz.');
      return;
    }

    if (cleanPass.length < 8) {
      setForgotError('Şifreniz en az 8 karakter uzunluğunda olmalıdır.');
      return;
    }

    if (cleanPass !== confirmPassword.trim()) {
      setForgotError('Girdiğiniz şifreler birbiriyle eşleşmiyor.');
      return;
    }

    setIsVerifying(true);
    try {
      const resetRes = await fetch('/api.php?action=reset_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          username: forgotUsername.trim(),
          resetToken,
          newPassword: cleanPass,
        }),
      });
      const data = await resetRes.json().catch(() => null);
      if (!resetRes.ok || !data?.success) {
        setForgotError(data?.message || 'Şifre sıfırlanamadı.');
        return;
      }
      setSuccessMessage('Şifreniz güvenli şekilde güncellendi. Yeni şifrenizle giriş yapabilirsiniz.');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setResetToken('');
      setMode('login');
    } catch (err) {
      console.warn('Password reset request error:', err);
      setForgotError('Sunucuya ulaşılamadı; şifre değiştirilmedi.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-center items-center p-4 py-8 sm:py-12 relative overflow-y-auto font-sans">
      {/* Subtle Background Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-600/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[300px] h-[300px] bg-indigo-600/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="w-full max-w-lg sm:max-w-xl relative z-10 space-y-6 my-auto">
        {/* Brand Header */}
        <div className="text-center space-y-3">
          {companyInfo.logoUrl ? (
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white p-2 text-white shadow-xl shadow-blue-500/20 ring-4 ring-blue-500/20 mb-1">
              <img
                src={companyInfo.logoUrl}
                alt={companyInfo.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white shadow-xl shadow-blue-500/20 ring-4 ring-blue-500/20 mb-1">
              <Wrench className="w-8 h-8" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-black tracking-tight text-white">{companyInfo.name}</h1>
            <p className="text-xs text-slate-400 mt-1 font-medium flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Teknik Servis Yönetim Paneli</span>
            </p>
          </div>
        </div>

        {/* Card Container */}
        <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-xl space-y-6">
          
          {/* 1. LOGIN MODE */}

          {mode === 'setup' && (
            <div className="bg-slate-900/85 border border-amber-500/30 rounded-2xl p-5 sm:p-7 shadow-2xl backdrop-blur-xl">
              <div className="flex items-start gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20"><Database className="w-6 h-6 text-amber-300" /></div>
                <div>
                  <h2 className="text-lg font-black text-white">Veritabanı Kurulumu</h2>
                  <p className="text-xs text-slate-400 mt-1">MySQL bağlantısı kurulmadan kullanıcı girişi açılmaz. Önce bağlantıyı güvenli şekilde kaydedin.</p>
                </div>
              </div>
              <form onSubmit={handleSetupDatabase} className="space-y-3">
                <label className="block text-xs font-bold text-slate-300">Kurulum Anahtarı
                  <input type="password" value={setupKey} onChange={e=>setSetupKey(e.target.value)} autoComplete="off" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-amber-400" placeholder="KURULUM_ANAHTARI.txt içindeki anahtar" />
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_110px] gap-3">
                  <label className="block text-xs font-bold text-slate-300">MySQL Host
                    <div className="relative mt-1"><Server className="absolute left-3 top-3 w-4 h-4 text-slate-500"/><input value={dbHost} onChange={e=>setDbHost(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-950 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-blue-400" placeholder="localhost" /></div>
                  </label>
                  <label className="block text-xs font-bold text-slate-300">Port
                    <input value={dbPort} onChange={e=>setDbPort(e.target.value)} inputMode="numeric" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-blue-400" />
                  </label>
                </div>
                <label className="block text-xs font-bold text-slate-300">Veritabanı Adı
                  <input value={dbName} onChange={e=>setDbName(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-blue-400" />
                </label>
                <label className="block text-xs font-bold text-slate-300">MySQL Kullanıcı Adı
                  <input value={dbUser} onChange={e=>setDbUser(e.target.value)} autoComplete="username" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-blue-400" />
                </label>
                <label className="block text-xs font-bold text-slate-300">MySQL Parolası
                  <input type="password" value={dbPassword} onChange={e=>setDbPassword(e.target.value)} autoComplete="new-password" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-blue-400" />
                </label>
                {setupError && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs font-semibold text-red-300">{setupError}</div>}
                {setupSuccess && <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-semibold text-emerald-300">{setupSuccess}</div>}
                <button type="submit" disabled={isVerifying} className="w-full rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-950 font-black py-3 text-sm flex items-center justify-center gap-2">
                  {isVerifying ? <Loader2 className="w-4 h-4 animate-spin"/> : <Database className="w-4 h-4"/>}
                  {isVerifying ? 'Bağlantı Test Ediliyor...' : 'Bağlan, Test Et ve Kaydet'}
                </button>
              </form>
              <p className="text-[11px] text-slate-500 mt-4">Kurulum anahtarını paket içindeki KURULUM_ANAHTARI.txt dosyasından alın. SQL bağlantısı kurulduktan sonra bu ekran kapanır.</p>
            </div>
          )}

          {mode === 'login' && (
            <>
              <div className="border-b border-slate-800 pb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-blue-400" />
                  Sistem Girişi
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Servis yönetim sistemine erişmek için bilgilerinizi girin.
                </p>
              </div>

              {sessionExpiredMessage && !error && (
                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs p-3.5 rounded-xl flex items-start space-x-2.5 animate-fadeIn">
                  <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span className="leading-relaxed font-medium">{sessionExpiredMessage}</span>
                </div>
              )}

              {error && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3.5 text-rose-300 text-xs flex items-start space-x-2.5 animate-fadeIn">
                  <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                  <span className="font-semibold">{error}</span>
                </div>
              )}

              {successMessage && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3.5 text-emerald-300 text-xs flex items-center space-x-2.5 animate-fadeIn">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="font-semibold">{successMessage}</span>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                {/* Username Input */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300 block">
                    Kullanıcı Adı
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      placeholder="Kullanıcı adınızı girin"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Password Input */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300 block">
                    Şifre
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Şifrenizi girin"
                      className="w-full pl-10 pr-10 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Options */}
                <div className="flex items-center justify-between text-xs pt-1">
                  <label className="flex items-center space-x-2 cursor-pointer text-slate-400 hover:text-slate-200">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                      className="rounded bg-slate-950 border-slate-700 text-blue-600 focus:ring-blue-500/20 w-4 h-4"
                    />
                    <span>Oturumumu açık tut</span>
                  </label>

                  <button
                    type="button"
                    onClick={() => {
                      setForgotError('');
                      setMode('forgot');
                    }}
                    className="text-blue-400 hover:text-blue-300 font-semibold transition-colors flex items-center gap-1"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                    <span>Şifremi Unuttum?</span>
                  </button>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isVerifying}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-75 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-600/30 flex items-center justify-center space-x-2 transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer disabled:cursor-not-allowed"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Doğrulanıyor...</span>
                    </>
                  ) : (
                    <>
                      <span>Giriş Yap</span>
                      <Check className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {/* 2. FORGOT PASSWORD - IDENTITY VERIFICATION MODE */}
          {mode === 'forgot' && (
            <>
              <div className="border-b border-slate-800 pb-4">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-amber-400" />
                    Şifre Kurtarma (Güvenli Giriş)
                  </h2>
                </div>
                <p className="text-xs text-slate-400">
                  Şifrenizi sıfırlamak için kullanıcı adınızı ve yöneticinin belirlediği gizli yanıtı girin.
                </p>
              </div>

              {forgotError && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3.5 text-rose-300 text-xs flex items-start space-x-2.5 animate-fadeIn">
                  <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                  <span className="font-semibold">{forgotError}</span>
                </div>
              )}

              <form onSubmit={handleVerifyIdentity} className="space-y-4">
                {/* Username Input */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300 block">
                    Kullanıcı Adı
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      value={forgotUsername}
                      onChange={e => setForgotUsername(e.target.value)}
                      placeholder="Kullanıcı adınız"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Security Question & Answer */}
                <div className="space-y-1.5 bg-slate-950/80 p-3.5 rounded-xl border border-slate-800">
                  <label className="text-xs font-bold text-amber-300 block">
                    Soru / Kriter: {companyInfo.securityQuestion || 'Yedek Kurtarma Kodu / Gizli Cevap'}
                  </label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      value={securityAnswerInput}
                      onChange={e => setSecurityAnswerInput(e.target.value)}
                      placeholder="Gizli güvenlik cevabınız veya kurtarma PIN'iniz"
                      className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-750 rounded-lg text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 font-medium"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setForgotError('');
                      setMode('login');
                    }}
                    className="flex-1 py-2.5 border border-slate-800 hover:bg-slate-800 text-slate-300 rounded-xl text-xs font-semibold flex items-center justify-center space-x-1.5 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Girişe Dön</span>
                  </button>

                  <button
                    type="submit"
                    className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-blue-600/30 flex items-center justify-center space-x-1.5 transition-all"
                  >
                    <span>Doğrula &amp; Devam Et</span>
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </>
          )}

          {/* 3. RESET NEW PASSWORD MODE */}
          {mode === 'reset' && (
            <>
              <div className="border-b border-slate-800 pb-4">
                <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-emerald-400" />
                  Yeni Şifre Belirleme
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Kimliğiniz başarıyla doğrulandı. Lütfen yeni giriş şifrenizi oluşturun.
                </p>
              </div>

              {forgotError && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3.5 text-rose-300 text-xs flex items-start space-x-2.5 animate-fadeIn">
                  <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                  <span className="font-semibold">{forgotError}</span>
                </div>
              )}

              {successMessage && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3.5 text-emerald-300 text-xs flex items-center space-x-2.5 animate-fadeIn">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="font-semibold">{successMessage}</span>
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 text-xs text-slate-300">
                  <span>Kullanıcı Adı: </span>
                  <strong className="text-white font-mono">{forgotUsername || companyInfo.adminUsername || 'admin'}</strong>
                </div>

                {/* New Password */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300 block">
                    Yeni Şifre
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Yeni şifrenizi girin"
                      className="w-full pl-10 pr-10 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium font-mono"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300 block">
                    Yeni Şifre (Tekrar)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Yeni şifrenizi tekrar girin"
                      className="w-full pl-10 pr-10 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium font-mono"
                    />
                  </div>
                </div>

                {/* Submit Reset */}
                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-600/30 flex items-center justify-center space-x-2 transition-all hover:scale-[1.01] cursor-pointer"
                >
                  <Check className="w-4 h-4" />
                  <span>Şifreyi Güncelle &amp; Giriş Yap</span>
                </button>
              </form>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="text-center text-[11px] text-slate-500">
          © {new Date().getFullYear()} {companyInfo.name} • Güvenli Giriş Paneli
        </div>
      </div>
    </div>
  );
};

